import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Search, Loader2, Calendar, Users, Wrench, AlertCircle, CheckCircle2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const YEARS = [2024, 2025, 2026, 2027];

/**
 * BulkPaymentModal — admin creates a SINGLE consolidated PaymentRecord per
 * dublador (and optionally staff) covering all selected projects.
 * The backend handles aggregation; we just collect inputs and POST /payment-records/bulk.
 */
const BulkPaymentModal = ({ open, onClose, onCreated, getAuthHeaders }) => {
  const now = new Date();
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [includeStaff, setIncludeStaff] = useState(false);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    setLoadingProjects(true);
    (async () => {
      try {
        const [activeRes, histRes] = await Promise.all([
          axios.get(`${API}/projects?status_filter=active`, { headers: getAuthHeaders() }),
          axios.get(`${API}/projects?status_filter=history`, { headers: getAuthHeaders() }),
        ]);
        if (cancel) return;
        const all = [...(activeRes.data || []), ...(histRes.data || [])];
        // De-dupe by id (in case lists overlap) and hide already-paid projects.
        const seen = new Set();
        const unique = all
          .filter(p => (seen.has(p.id) ? false : (seen.add(p.id), true)))
          .filter(p => p.payment_status !== 'pago');
        setProjects(unique);
      } catch {
        if (!cancel) toast.error('Erro ao carregar projetos');
      } finally {
        if (!cancel) setLoadingProjects(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, getAuthHeaders]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setSearch('');
      setIncludeStaff(false);
      setSubmitting(false);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(p =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.client || '').toLowerCase().includes(q) ||
      (p.status || '').toLowerCase().includes(q)
    );
  }, [projects, search]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(p => selected.has(p.id));
  const toggleAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach(p => next.delete(p.id));
      else filtered.forEach(p => next.add(p.id));
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0) {
      toast.error('Selecione ao menos um projeto.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/payment-records/bulk`,
        {
          project_ids: Array.from(selected),
          year,
          month,
          include_staff: includeStaff,
        },
        { headers: getAuthHeaders() },
      );
      const created = res.data?.created ?? 0;
      const skipped = res.data?.skipped || [];
      toast.success(
        `${created} pagamento(s) criado(s) em lote.${skipped.length ? ` (${skipped.length} ignorado(s) — ver console)` : ''}`,
      );
      if (skipped.length) {
        // eslint-disable-next-line no-console
        console.warn('Bulk payment skipped:', skipped);
      }
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar pagamentos em lote.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bulk-payment-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-700 flex items-start justify-between gap-3 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">Pagamento em Lote</h2>
            <p className="text-xs text-slate-400 mt-1">
              Selecione 1 ou mais projetos. Será criado <strong>1 pagamento por dublador</strong> consolidando todos os projetos selecionados.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="bulk-payment-close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {/* Period + options */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                <Calendar className="h-3 w-3 inline mr-1" /> Mês
              </label>
              <select
                value={month}
                onChange={e => setMonth(parseInt(e.target.value, 10))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                data-testid="bulk-payment-month"
              >
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Ano</label>
              <select
                value={year}
                onChange={e => setYear(parseInt(e.target.value, 10))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                data-testid="bulk-payment-year"
              >
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none" data-testid="bulk-payment-include-staff-label">
                <input
                  type="checkbox"
                  checked={includeStaff}
                  onChange={e => setIncludeStaff(e.target.checked)}
                  className="h-4 w-4 accent-teal-500"
                  data-testid="bulk-payment-include-staff"
                />
                <Wrench className="h-4 w-4 text-purple-400" />
                Incluir equipe (staff)
              </label>
            </div>
          </div>

          {/* Search + select all */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar projeto…"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                data-testid="bulk-payment-search"
              />
            </div>
            <button
              type="button"
              onClick={toggleAllFiltered}
              disabled={filtered.length === 0}
              className="text-xs bg-slate-800 border border-slate-700 hover:border-teal-500/50 text-slate-300 hover:text-teal-300 px-3 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bulk-payment-toggle-all"
            >
              {allFilteredSelected ? 'Desmarcar todos' : 'Selecionar todos'}
              {search && <span className="ml-1 text-slate-500">(filtrados)</span>}
            </button>
          </div>

          {/* Project list */}
          <div className="border border-slate-700 rounded-lg overflow-hidden">
            {loadingProjects ? (
              <div className="px-3 py-8 text-center text-slate-400 flex items-center justify-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Carregando projetos…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-8 text-center text-slate-500 text-sm">
                Nenhum projeto encontrado.
              </div>
            ) : (
              <ul className="max-h-72 overflow-y-auto divide-y divide-slate-800">
                {filtered.map(p => {
                  const isSelected = selected.has(p.id);
                  return (
                    <li key={p.id}>
                      <label
                        className={`flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                          isSelected ? 'bg-teal-500/10' : 'hover:bg-slate-800/60'
                        }`}
                        data-testid={`bulk-payment-project-row-${p.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(p.id)}
                          className="h-4 w-4 accent-teal-500 shrink-0"
                          data-testid={`bulk-payment-project-checkbox-${p.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{p.title}</p>
                          <p className="text-[11px] text-slate-500 truncate">
                            {[p.client, p.status, p.duration_display].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        <span
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                            p.status === 'Concluído'
                              ? 'bg-emerald-500/15 text-emerald-300'
                              : 'bg-amber-500/15 text-amber-300'
                          }`}
                        >
                          {p.status || '—'}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Summary */}
          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-xs text-slate-400 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-teal-400 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p className="text-slate-200">
                <strong className="text-teal-300">{selected.size}</strong> projeto(s) selecionado(s) ·{' '}
                Período: <strong>{MONTHS[month - 1]} {year}</strong>
              </p>
              <p>
                <Users className="h-3 w-3 inline mr-1" />
                Será criado <strong>1 pagamento por dublador</strong>
                {includeStaff && ' + 1 por staff'}, com o nome do projeto = lista dos projetos selecionados e valor = soma das contribuições (vozerios pagam valor integral a cada participante).
              </p>
            </div>
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-slate-700 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
            data-testid="bulk-payment-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || selected.size === 0}
            className="px-5 py-2 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            data-testid="bulk-payment-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {submitting ? 'Criando…' : `Criar ${selected.size || ''} pagamento(s) em lote`}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default BulkPaymentModal;
